import { DateTime } from 'luxon';

import { ThProps } from '@/components/uiParts';
import { CurrencyDomainService, DateDomainService } from '@/domain/services';
import {
  MosPosNodeType,
  MosPosOrderStatus,
  MosPosStore,
} from '@/services/MosPosOrder/type';

/** 1ページあたりの表示件数。共通の LIMIT_PER_PAGE (50) ではなく本画面固有の値。 */
export const ITEMS_PER_PAGE = 10;

export const LIST_QUERY_KEY = 'mos-pos-orders-list';
export const DETAIL_QUERY_KEY = 'mos-pos-order-detail';
export const STORE_LIST_QUERY_KEY = 'mos-pos-orders-store-list';

/** 一覧・詳細から遷移したあとのトースト表示時間 */
export const SUCCESS_TOAST_DURATION_MS = 3000;
export const ERROR_TOAST_DURATION_MS = 5000;

// NOTE 再送信可能期間 (注文から48時間) の判定はブラウザ時計だとサーバー側 guard とずれるため、
//      FE では計算せず API の can_resend / can_cancel をそのまま使う。

/** 再送信 / 取消の操作対象になりうるステータス。これ以外は競合として扱う。 */
export const ACTIONABLE_STATUSES: MosPosOrderStatus[] = [
  'RECOVERABLE_ERROR',
  'POS_FAILED_FINAL',
];

export const isActionableStatus = (status: MosPosOrderStatus): boolean =>
  ACTIONABLE_STATUSES.includes(status);

/**
 * BE は enum をそのまま返すため、日本語ラベルは FE の責務。
 * NOTE このテーブルは BE ドキュメントの状態遷移表と対になっている。
 *      ステータスを追加する際は双方を同時に更新すること。
 */
export const orderStatusLabel: Record<MosPosOrderStatus, string> = {
  ORDER_RECEIVED: '注文送信前',
  SENDING_TO_POS: '注文送信前',
  SENT_TO_POS: '注文送信完了',
  RESENT_TO_POS: '注文送信完了',
  POS_ACCEPTED: '注文受付完了',
  POS_FAILED_RETRYING: '注文送信失敗',
  POS_FAILED_FINAL: '注文送信失敗',
  RECOVERABLE_ERROR: 'その他注文エラー',
  UNRECOVERABLE_ERROR: 'その他注文エラー',
  CANCELLED: '注文キャンセル',
  // FDS 側の取消通知 (order.canceled) による終端。CANCELLED (Camel が取消 API を
  // 呼んだ結果) とは別値だが、画面上は同じ「注文キャンセル」で足りる。
  FDS_CANCELLED: '注文キャンセル',
  RESEND_CANCELLED: '注文キャンセル',
  DEMAECAN_OP_HANDLING: '注文キャンセル',
};

type StatusTone = 'green' | 'blue' | 'gray' | 'red';

/** ステータスバッジの配色 (設計書指定値) */
export const orderStatusColor: Record<
  StatusTone,
  { bg: string; color: string; dot: string }
> = {
  green: { bg: '#F0FFF4', color: '#2F855A', dot: '#2F855A' },
  blue: { bg: '#EBF8FF', color: '#2B6CB0', dot: '#2B6CB0' },
  gray: { bg: '#F7FAFC', color: '#4A5568', dot: '#4A5568' },
  red: { bg: '#FFF5F5', color: '#C53030', dot: '#C53030' },
};

export const orderStatusTone: Record<MosPosOrderStatus, StatusTone> = {
  ORDER_RECEIVED: 'gray',
  SENDING_TO_POS: 'gray',
  SENT_TO_POS: 'blue',
  RESENT_TO_POS: 'blue',
  POS_ACCEPTED: 'green',
  POS_FAILED_RETRYING: 'red',
  POS_FAILED_FINAL: 'red',
  RECOVERABLE_ERROR: 'red',
  UNRECOVERABLE_ERROR: 'red',
  CANCELLED: 'gray',
  FDS_CANCELLED: 'gray',
  RESEND_CANCELLED: 'gray',
  DEMAECAN_OP_HANDLING: 'gray',
};

export const UBER_EATS_DELIVERY_SERVICE_ID = 1;
export const DEMAECAN_DELIVERY_SERVICE_ID = 10;

/** デリバリーサービスバッジ。CmTag は colorScheme 前提のため bg / color を直接指定する。 */
export const deliveryServiceBadge: Record<
  number,
  { label: string; bg: string; color: string }
> = {
  [UBER_EATS_DELIVERY_SERVICE_ID]: {
    label: 'Uber Eats',
    bg: '#1D1D1D',
    color: '#06C167',
  },
  [DEMAECAN_DELIVERY_SERVICE_ID]: {
    label: '出前館',
    bg: '#FFF4E6',
    color: '#E85C2B',
  },
};

export const deliveryServiceOptions = [
  { value: '', label: 'すべて' },
  { value: String(UBER_EATS_DELIVERY_SERVICE_ID), label: 'Uber Eats' },
  { value: String(DEMAECAN_DELIVERY_SERVICE_ID), label: '出前館' },
];

/* 店舗検索 (画面設計書 No.20) */

export const STORE_SEARCH_PLACEHOLDER = '店舗を検索';
export const ALL_STORES_OPTION_LABEL = 'すべての店舗';
export const NO_STORE_CANDIDATES_LABEL = '対象拠点なし';
export const NO_MATCHING_STORE_LABEL = '該当する店舗はありません';
/** 候補の取得失敗時の表示。画面設計書に定めがないため、0 件の「対象拠点なし」と区別できる文言にしている */
export const STORE_LOAD_ERROR_LABEL = '店舗を取得できませんでした';
/** 一致した文字のハイライト配色 (画面設計書 No.20 モック準拠) */
export const STORE_MATCH_HIGHLIGHT_BG = '#FFF3C4';
/** 候補リストの最大高さ (モックの .ac-panel) */
export const STORE_MENU_MAX_HEIGHT = 252;
/** 先頭の「すべての店舗」と店舗候補の区切り線 (モックの .ac-opt.ac-all) */
export const ALL_STORES_OPTION_BORDER_COLOR = '#EDF2F7';
/** 候補 1 件以下・取得失敗時の固定表示 (モックの .ac-fixed) */
export const STORE_FIXED_BG = '#F7FAFC';
export const STORE_FIXED_BORDER_COLOR = '#E2E8F0';
export const STORE_FIXED_COLOR = '#718096';

/** 候補の表示・照合文字列は「{mos_store_code} - {auth_store_name}」 */
export const formatStoreLabel = (store: MosPosStore): string =>
  `${store.mos_store_code} - ${store.auth_store_name}`;

export type SubsequenceMatch = {
  /** query の全文字を label 内で入力順どおりに見つけられたか */
  hit: boolean;
  /** ハイライト対象になった label 内の文字インデックス */
  matchedIndices: number[];
};

/**
 * 入力文字を入力順に照合する部分列一致 (大文字小文字は無視する)。
 * 例えば query = "abc" は label 内に a→b→c の順で (連続していなくても) 現れれば一致する。
 */
export const matchSubsequence = (
  label: string,
  query: string
): SubsequenceMatch => {
  if (query === '') return { hit: true, matchedIndices: [] };

  const chars = label.split('');
  const lowerChars = chars.map((char) => char.toLowerCase());
  const queryChars = query.toLowerCase().split('');
  const matchedIndices: number[] = [];
  let queryIndex = 0;

  chars.forEach((_, index) => {
    if (
      queryIndex < queryChars.length &&
      lowerChars[index] === queryChars[queryIndex]
    ) {
      matchedIndices.push(index);
      queryIndex += 1;
    }
  });

  return { hit: queryIndex === queryChars.length, matchedIndices };
};

/** ボタン色は設計レビューで意図的に反転させている。逆にしないこと。 */
/** 再送信 = 非破壊操作なのでグレー */
export const RESEND_BUTTON_BG = '#718096';
export const RESEND_BUTTON_HOVER_BG = '#5D6B80';
/** 取消 = 破壊操作なので赤 (theme.primaryRed と同値) */
export const CANCEL_BUTTON_BG = '#EB4458';

export const ROW_HOVER_BG = '#FAFBFC';
export const PAYMENT_AMOUNT_COLOR = '#EB4458';
export const DISCOUNT_AMOUNT_COLOR = '#C53030';
export const CHILD_ITEM_COLOR = '#718096';
export const NOTES_BG = '#F7FAFC';
/** 商品リストフッターの小計行 (画面設計書 No.55) */
export const ITEM_FOOTER_SUBTOTAL_BG = '#F7FAFC';
export const ITEM_FOOTER_BORDER_COLOR = '#E2E8F0';
/** 商品リストフッターの合計行 (画面設計書 No.57) */
export const ITEM_FOOTER_TOTAL_BG = '#EBF4FF';
export const ITEM_FOOTER_TOTAL_COLOR = '#394867';
/** アクションフッターの見出し (画面設計書 No.58) */
export const ACTION_NOTE_TITLE_COLOR = '#EB4458';
/** SCR002 / SCR003 の項目行 (テンプレートの .field / .lbl) */
export const FIELD_LABEL_WIDTH = '190px';
export const FIELD_LABEL_COLOR = '#718096';
export const FIELD_SEPARATOR_COLOR = '#EDF2F7';
/** セクション見出しの帯 (テンプレートの .section-head) */
export const SECTION_HEAD_BG = '#F7FAFC';
export const SECTION_HEAD_BORDER_COLOR = '#E2E8F0';
/** 見出し先頭のアイコン (テンプレートの .section-head .material-symbols-outlined) */
export const SECTION_HEAD_ICON_COLOR = '#4A5568';
export const SECTION_HEAD_ICON_SIZE = '18px';
export const SECTION_HEAD_GAP = '8px';
/** SCR002 / SCR003 の確認バナー (画面設計書 No.5。両画面で同じ警告配色) */
export const CONFIRM_BANNER_BG = '#FFFAF0';
export const CONFIRM_BANNER_BORDER_COLOR = '#F6AD55';
export const CONFIRM_BANNER_ACCENT_COLOR = '#DD6B20';

/** 受取条件は API が返さないため FE で固定値を表示する。 */
export const PICKUP_CONDITION_LABEL = '外部提携社注文';

export const EMPTY_VALUE = '-';
const JST_TIME_ZONE = 'Asia/Tokyo';

// SCR001 の列は画面設計書 No.8 / 10 / 11 / 12 / 19 / 13 / 14・15 の 7 列。
// 注文ステータス (No.9) は SCR002 / SCR003 の項目なので一覧には出さない。
export const listTableThs: ThProps[] = [
  { label: 'WebオーダーID', minWidth: '160px', position: 'left' },
  { label: '店舗', minWidth: '240px', position: 'left' },
  { label: 'デリバリーサービス', minWidth: '140px', position: 'left' },
  { label: '注文日時', minWidth: '150px', position: 'left' },
  { label: '配達員到着予定時刻', minWidth: '150px', position: 'left' },
  // 画面設計書 No.13 の「右寄せ」は金額セルに適用します。テンプレートの CSS は
  // `td.amount, th.amount { text-align:right; }` と列見出しも対象にしていますが、
  // 見出しが他の列と揃わず読みにくいというテスト FB (#2593 ID-4) を受けて、
  // 見出しは左寄せに統一しました。セルの右寄せは MosPosOrderTable 側で指定しています。
  { label: '売上', minWidth: '100px', position: 'left' },
  { label: '操作', minWidth: '160px', position: 'left' },
];

export const itemTableThs: ThProps[] = [
  { label: '商品コード', minWidth: '120px', position: 'left' },
  { label: '商品名', minWidth: '260px', position: 'left' },
  { label: '数量', minWidth: '60px', position: 'right' },
  { label: '単価', minWidth: '90px', position: 'right' },
  { label: '小計', minWidth: '90px', position: 'right' },
];

/**
 * 明細行の金額表示ルール。
 * accessory はマスタ上の価格が常に ¥0 で、そのまま ¥0 と出すと不具合に見えるため金額を出さない。
 */
export const shouldShowItemPrice = (nodeType: MosPosNodeType): boolean =>
  nodeType !== 'accessory';

/** set_parent / accessory は単価の概念がない (unit_price = null) */
export const shouldShowItemUnitPrice = (
  nodeType: MosPosNodeType,
  unitPrice: number | null
): boolean => unitPrice !== null && shouldShowItemPrice(nodeType);

export const formatPrice = (value: number): string =>
  CurrencyDomainService.priceFormat(value);

/**
 * 値引は絶対値にマイナス記号を付けて表示する。
 * 画面設計書 No.39 / No.56 は表示形式を「-¥{金額}」と無条件に定めているため、
 * 0 のとき (出前館は常に 0) も「-￥0」と表示する。
 */
export const formatDiscountPrice = (value: number): string =>
  `-${CurrencyDomainService.priceFormat(Math.abs(value))}`;

export const formatDateTime = (value: string | null): string =>
  value === null
    ? EMPTY_VALUE
    : DateTime.fromISO(value)
        .setZone(JST_TIME_ZONE)
        .toFormat('yyyy/MM/dd HH:mm');

export const formatDateTimeWithSeconds = (value: string): string =>
  DateTime.fromISO(value)
    .setZone(JST_TIME_ZONE)
    .toFormat('yyyy/MM/dd HH:mm:ss');

export const formatDay = (value: string): string =>
  DateTime.fromISO(value).setZone(JST_TIME_ZONE).toFormat('yyyy/MM/dd');

export const formatTime = (value: string): string =>
  DateTime.fromISO(value).setZone(JST_TIME_ZONE).toFormat('HH:mm');

/** API へ渡す日付 ('YYYY-MM-DD') */
export const formatApiDate = (date: Date): string =>
  DateDomainService.format(date);

export const displayOrEmpty = (value: string | null | undefined): string =>
  value === null || value === undefined || value === '' ? EMPTY_VALUE : value;

export type PageNumberSlot = number | 'ellipsis';

/** 一度に並べるページ番号の上限。これを超えたら「…」で省略する */
const MAX_VISIBLE_PAGE_NUMBERS = 7;
/** 現在ページの前後に必ず残すページ数 */
const ADJACENT_PAGE_COUNT = 1;

/**
 * ページ番号ボタンの並びを組み立てる。
 * 全ページ分を並べると注文が数百件あるときにボタンが折り返して崩れるため、
 * 先頭・末尾・現在ページの前後だけを残して「…」で省略する。
 */
export const buildPageNumbers = (
  currentPage: number,
  totalPages: number
): PageNumberSlot[] => {
  if (totalPages <= MAX_VISIBLE_PAGE_NUMBERS) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const windowStart = Math.max(2, currentPage - ADJACENT_PAGE_COUNT);
  const windowEnd = Math.min(totalPages - 1, currentPage + ADJACENT_PAGE_COUNT);
  const slots: PageNumberSlot[] = [1];

  // 省略対象が 1 ページだけなら「…」より番号を出したほうが短い
  if (windowStart > 2) {
    slots.push(windowStart === 3 ? 2 : 'ellipsis');
  }
  for (let page = windowStart; page <= windowEnd; page += 1) {
    slots.push(page);
  }
  if (windowEnd < totalPages - 1) {
    slots.push(windowEnd === totalPages - 2 ? totalPages - 1 : 'ellipsis');
  }
  slots.push(totalPages);

  return slots;
};

export type FilterForm = {
  /** '' は「すべて」 */
  deliveryServiceId: string;
  /** null は「すべての店舗」(絞り込みなし) */
  authStoreId: number | null;
  orderDateStart: Date;
  orderDateEnd: Date;
};

/**
 * 開始日の既定値は「当日 - 2日」。
 * 当日にすると48時間を超えて取消が必要な注文が初期表示で見えなくなるため。
 */
export const DEFAULT_ORDER_DATE_START_OFFSET_DAYS = 2;

export const createDefaultFilterForm = (now: Date = new Date()): FilterForm => {
  const endJst = DateTime.fromJSDate(now).setZone(JST_TIME_ZONE);
  const startJst = endJst.minus({ days: DEFAULT_ORDER_DATE_START_OFFSET_DAYS });
  // CmDatePicker はローカル Date の年月日をカレンダー値として扱う。
  const toCalendarDate = (value: DateTime): Date =>
    new Date(value.year, value.month - 1, value.day);
  return {
    deliveryServiceId: '',
    authStoreId: null,
    orderDateStart: toCalendarDate(startJst),
    orderDateEnd: toCalendarDate(endJst),
  };
};

export type ToastLocationState = {
  toast?: {
    type: 'resend' | 'cancel';
    webOrderId: string;
  };
};

export type DetailMode = 'resend' | 'cancel';
